import { Deal } from "../../../../shared/cubiq-model-multi-asset/model/Deal";
import {
  ApplicationRef,
  Component,
  ElementRef,
  Injector,
  Input,
  NgZone,
  ViewEncapsulation,
  OnInit,
  AfterViewInit,
  ViewChild,
} from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  ProfilePromiseActionService,
  SystemConfigPromiseService,
  BfxBlotterAction,
  Temperature,
  BmarkSpotRiskTableDefinitionFactory,
  ProfileSettingKeys,
  TraderGroupLookupService,
  SysFeatures,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { ReplaySubject, Subject, Subscription } from "rxjs";
import { BfxBaseBlotter, provideBlotter } from "../../../bfx-base-blotter/bfx-base-blotter";
import { BfxBaseBlotterColumnSelectorService } from "../../../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterFilterService } from "../../../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxMouseModifierKeyService } from "../../../bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxSaveProfileNotificationService } from "../../../bfx-common/services/bfx-save-profile-notification.service";
import { BfxUserProfileService } from "../../../bfx-common/services/bfx-user-profile.service";
import { GoldenLayoutService } from "../../../golden-layout/golden-layout-service";
import { GridOptions } from "ag-grid-community/dist/lib/entities/gridOptions";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";
import { BmarkClientOrdersBlotterMode } from "../../../../shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { BmarkSpotRiskDataService } from "./services/bmark-spot-risk-data.service";
import { BmarkSpotRiskFilteringService } from "./services/bmark-spot-risk-filtering.service";
import { BmarkClientOrdersContextMenuService } from "../client-orders/services/bmark-client-orders-context-menu.service";
import { BmarkSpotRiskInstrumentRendererComponent } from "./renderers/bmark-spot-risk-instrument-renderer.component";
import { BmarkSpotRiskTotalsRendererComponent } from "./renderers/bmark-spot-risk-totals-renderer.component";
import { BmarkSpotRiskTraderGroupRendererComponent } from "./renderers/bmark-spot-risk-trader-group-renderer.component";
import { AmountViewType } from "app/bfx-menus/bfx-blotter-settings-menu.component";
import { BmarkSpotRiskContextMenuService } from "./services/bmark-spot-risk-context-menu.service";
import {
  PROFILE_SETTINGS_EXTCOLOUR_HIGHLIGHTING,
  SETTINGS_PROPERTY_AMOUNTVIEWTYPE,
} from "app/bfx-user-settings/bfx-bmark-summary-settings-preferences.component";
import { BmarkBlotterViewFilterSelectionService } from "../../services/bmark-blotter-view-filter-selection.service";
import { TgStatusColumnHeaderComponent } from "./TgStatusColumnHeaderComponent";
import { Column, Optional } from "ag-grid-community";
import { COLUMN_BMARK_TOTAL_EXTERNAL } from "shared/cubiq-services/model/BmarkSpotRiskTableDefinitionFactory";
import {
  COLUMN_ICONS,
  COLUMN_SELECTION,
} from "shared/cubiq-services/model/TableDefinitionFactory.constants";
import { DomHandler } from "@bfx/primeng/dom";
import { BFXDomHelper } from "app/bfx-common/helpers/bfx-dom-helper";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { OverlayPanel } from "@bfx/primeng/overlaypanel";
import { BfxBaseBlotterContextMenuService } from "app/bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { BaseBMarkRiskTableDefinitionFactory } from "shared/cubiq-services/model/BaseBmarkRiskTableDefinitionFactory";
import { BmarkBaseRiskDataService } from "app/bfx-bmark-summary/blotters/spot-risk/services/bmark-base-risk-data.service";

export const RISK_ROW_HEIGHT: number = 35;

/**
 * Generic abstract blotter view used as base for both spot-based and fwd based benhcmark risk. Services specific
 * to risk type are provided in the blotter defn, data service, and context menu service.
 */
@Component({
  selector: "bmark-base-risk-blotter",
  template: "<div></div>",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export abstract class BmarkBaseRiskBlotterComponent
  extends BfxBaseBlotter
  implements AfterViewInit
{
  public mode = OrdersTableMode.DEALT;

  public isIntentStyled: boolean = true;
  public isFullTempStyle: boolean = true;

  public frameworkComponents: any = {
    bmarkRiskInstrumentRenderer: BmarkSpotRiskInstrumentRendererComponent,
    bmarkSpotRiskTotalsRenderer: BmarkSpotRiskTotalsRendererComponent,
    bmarkSpotRiskTraderGroupRenderer: BmarkSpotRiskTraderGroupRendererComponent,
    tgStatusColumnHeaderComponent: TgStatusColumnHeaderComponent,
  };

  @Input()
  public orderIDsFromInput: any;

  /** Fixing name used for Overlay */
  @Input() public fixingName: string;

  constructor(
    @Optional() protected _traderGroupLookup: TraderGroupLookupService,
    private bfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BmarkBaseRiskDataService,
    zone: NgZone,
    _tableDefinitionFactory: BaseBMarkRiskTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    userProfileService: BfxUserProfileService,
    _contextMenuServices: BfxBaseBlotterContextMenuService<any>,
  ) {
    super(
      bfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuServices,
      zone,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      null, // filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
    );

    this.blotterId = this.constructor.name;
  }

  ngAfterViewInit() {
    this.updateGridData();
  }

  protected createsGridOptions(): GridOptions {
    let gridOptions = super.createsGridOptions();
    gridOptions.rowClassRules = {
      hot: this.checkHotStyle.bind(this),
      rowFlash: function (params) {
        return params.data.rowFlash;
      },
    };

    const doc = DomHandler.getDocument(this.bfxBlotterElement);
    const rowHeight = BFXDomHelper.getVariablePx(doc, "--ds-cont-sizing-common-md", 30);
    (gridOptions.getRowHeight = function (params) {
      return rowHeight;
    }),
      (gridOptions.rowHeight = 32);
    gridOptions.defaultColDef = {
      sortable: true,
    };
    gridOptions.context = {
      mode: this.service.getBlotterMode.bind(this.service),
    };

    return gridOptions;
  }

  protected initializeInMemory(): void {
    super.initializeInMemory();
  }

  protected getBadgeNumber(): number {
    return this.service.getItemCount();
  }

  public setBmarkViewType(tableView: BmarkClientOrdersBlotterMode): void {
    this.service.setBlotterMode(tableView);
  }
  protected get dealtMode(): boolean {
    // don't use in template
    return OrdersTableMode.DEALT === this.mode || OrdersTableMode.NDF_DEALT === this.mode;
  }

  protected set dealtMode(isDealt: boolean) {
    if (isDealt === true && OrdersTableMode.BASE === this.mode) {
      this.mode = OrdersTableMode.DEALT;
    } else if (isDealt === true && OrdersTableMode.NDF_BASE === this.mode) {
      this.mode = OrdersTableMode.NDF_DEALT;
    } else if (OrdersTableMode.DEALT === this.mode) {
      this.mode = OrdersTableMode.BASE;
    } else if (OrdersTableMode.NDF_DEALT === this.mode) {
      this.mode = OrdersTableMode.NDF_BASE;
    }
  }

  /****************
   * click events *
   ****************/

  public onCellDoubleClicked(event) {
    if (
      event == Object(event) &&
      event.node === Object(event.node) &&
      typeof event.node.id === "string"
    ) {
      let id =
        event.node.data instanceof CubiqTS.OrderDataImpl &&
        event.node.data.blotterMode === BmarkClientOrdersBlotterMode.RISK
          ? event.node.data.orderId.toString()
          : event.node.id;
      //this._contextMenuServices.fireActionEvent(BfxBlotterAction.ACTION_VIEW_DETAILS, this, [id], [event.node.data], null, null, null, event);
    }
  }

  /**
   *  maybe needed later
   *
   * public showSettings(event: Event): void {
   *  this.layoutService.showSettings('Benchmark', event);
   *   }
   */

  /** HELPER FUNCTIONS **/

  private checkHotStyle(params): boolean {
    let isHot = false;

    switch (params.data.orderState) {
      case CubiqTS.OrderState.FILL_EXPIRED:
        isHot = true;
        break;
      case CubiqTS.OrderState.FILL_ERROR:
        isHot = true;
        break;
      default:
        break;
    }
    return isHot;
  }

  public setAmountViewType(type: AmountViewType) {
    this.service.setAmountViewType(type);
  }

  public getAmountViewType(): AmountViewType {
    return this.service.getAmountViewType();
  }

  /**********************************************************************/
  /* BLOTTER - PROFILE */

  public abstract getBlotterType(): CubiqTS.BlotterType;

  /*     public getComponentConfiguration(): any {
        return super.getComponentConfiguration();
    } */

  public abstract getBlotterTypeStr(): string;

  public getComponentConfiguration(): any {
    let ret = super.getComponentConfiguration();
    ret[SETTINGS_PROPERTY_AMOUNTVIEWTYPE] = this.getAmountViewType();
    return ret;
  }

  /**
   * Without this, the base blotter will apply a filter depending on state of blotter filter bar.
   * Data in the Spot risk model is already filtered before it come into the service
   * and changes to filter are propagated to the service from the other proviers at the momenet so we don't
   * want to do any additional filtering here
   * @returns
   */
  public override isExternalFilterPresent(): boolean {
    return false;
  }

  /**
   * Spot view supports external, fwd doesn't so let this be overriden
   * @param colDef
   */
  protected abstract isExternalColumn(colDef: BfxColDef): boolean;
}
